// packages import
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import moment from 'moment'
import { FC, useContext, useEffect, useRef, useState } from 'react'

// components import
import { CardBox } from '../../../theme/styleComponents'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { Alert } from '../../common/Alert'

// others
import {
  Bill,
  BillStatus,
  useDownloadBillSnapshotLazyQuery,
  useRegenerateBillClientCopyMutation
} from '../../../../generated'
import { AuthContext } from '../../../context'

interface ClientBillCopyWidgetProps {
  bill?: Bill | null
  onRefetch?: () => void
}

const COPY_SOURCE_LABELS: Record<string, string> = {
  finalization: 'Bill conversion',
  regenerated: 'Regenerated'
}

// Statuses for which a final client copy makes sense (mirrors the "Download Original" gating).
const CLIENT_COPY_STATUSES = [
  BillStatus.Billed,
  BillStatus.SentToClient,
  BillStatus.PartiallyPaid,
  BillStatus.Paid,
  BillStatus.Forwarded
]

const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 12

const ClientBillCopyWidget: FC<ClientBillCopyWidgetProps> = ({ bill, onRefetch }) => {
  const { isAccountant, isAdmin, isSuperAdmin } = useContext(AuthContext)
  const canGenerate = !!(isAccountant || isAdmin || isSuperAdmin)

  const [downloadBillSnapshot] = useDownloadBillSnapshotLazyQuery({ fetchPolicy: 'network-only' })
  const [regenerateBillClientCopy] = useRegenerateBillClientCopyMutation()

  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null)

  const billCopies = bill?.billCopies ?? []
  const hasCopies = billCopies.length > 0

  // Polling state for async (BullMQ) generation: wait until a new copy shows up in billCopies.
  const targetCountRef = useRef<number>(0)
  const pollCountRef = useRef<number>(0)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    setGenerating(false)
  }

  useEffect(() => {
    // A new copy landed — generation finished.
    if (generating && billCopies.length >= targetCountRef.current) {
      stopPolling()
      Alert.success('New client bill copy generated.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billCopies.length])

  useEffect(() => () => stopPolling(), [])

  if (!bill?.status || !CLIENT_COPY_STATUSES.includes(bill.status)) return null

  const billId = typeof bill.id === 'number' ? bill.id : Number(bill.id)
  if (!Number.isFinite(billId)) return null

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await onRefetch?.()
    } finally {
      // Brief spinner so the refresh reads as an action even when the refetch resolves instantly.
      setTimeout(() => setRefreshing(false), 600)
    }
  }

  const handleDownload = async (copyIndex: number) => {
    setDownloadingIndex(copyIndex)
    try {
      const { data } = await downloadBillSnapshot({ variables: { billId, copyIndex } })
      const result = data?.downloadBillSnapshot
      if (result?.preSignedUrl) {
        window.open(result.preSignedUrl, '_blank')
      } else {
        Alert.info(result?.response?.message || 'This copy is not available yet.')
      }
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to download bill copy')
    } finally {
      setDownloadingIndex(null)
    }
  }

  const startPolling = () => {
    targetCountRef.current = billCopies.length + 1
    pollCountRef.current = 0
    setGenerating(true)
    pollTimerRef.current = setInterval(() => {
      pollCountRef.current += 1
      onRefetch?.()
      if (pollCountRef.current >= MAX_POLLS) {
        stopPolling()
        Alert.info('Still generating — refresh the page in a moment to see the new copy.')
      }
    }, POLL_INTERVAL_MS)
  }

  const handleConfirmGenerate = async () => {
    setSubmitting(true)
    try {
      const { data } = await regenerateBillClientCopy({ variables: { billId } })
      const response = data?.regenerateBillClientCopy?.response
      setShowConfirm(false)
      Alert.info(response?.message || 'A new client bill copy is being generated.')
      startPolling()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate client bill copy')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <CardBox mb={3}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E4E7EC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="h6" fontWeight={400} color="gray900" fontSize="18px">
            Client Bill Copy
          </Typography>
          <Tooltip title="Refresh copies">
            <span>
              <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {canGenerate && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowConfirm(true)}
            disabled={generating || submitting}
            endIcon={generating && <CircularProgress size={16} color="inherit" />}
          >
            {generating ? 'Generating…' : 'Generate Client Copy'}
          </Button>
        )}
      </Box>

      {!hasCopies ? (
        <Box px={3.5} py={3}>
          <Typography variant="subtitle1" color="secondary.light">
            No client copy has been generated yet.
            {canGenerate ? ' Use “Generate Client Copy” to create one from the bill’s current state.' : ''}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
          {billCopies.map((copy, index) => (
            <Box
              key={`${copy.generatedAt}-${index}`}
              px={3.5}
              py={2}
              borderBottom="1px solid #E4E7EC"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography color="secondary.light" fontWeight={500}>
                    {copy.generatedAt ? moment(copy.generatedAt).format('MMM DD, YYYY - h:mm A') : 'Unknown date'}
                  </Typography>
                  {index === 0 && <Chip label="Active" size="small" color="success" variant="outlined" />}
                </Box>
                <Typography variant="subtitle1" color="secondary.light">
                  {COPY_SOURCE_LABELS[copy.source || ''] || 'Generated'}
                  {' · '}
                  {copy.generatedByName || 'System'}
                </Typography>
              </Box>
              <Button
                variant="text"
                size="small"
                startIcon={
                  downloadingIndex === index ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                }
                disabled={downloadingIndex === index}
                onClick={() => handleDownload(index)}
              >
                Download
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <ConfirmationModal
        isOpen={showConfirm}
        title="Generate New Client Copy"
        message={
          hasCopies
            ? 'A bill copy already exists (generated at bill conversion). Generating a new one will create an updated client copy reflecting the latest payments and trust applied, while keeping the existing copy in the history. Continue?'
            : 'Generate a client copy of this bill from its current state (including any payments and trust applied)?'
        }
        confirmText="Generate New Copy"
        cancelText="Cancel"
        isLoading={submitting}
        variant="danger"
        onConfirm={handleConfirmGenerate}
        onCancel={() => setShowConfirm(false)}
      />
    </CardBox>
  )
}

export default ClientBillCopyWidget
